<template>
  <div id="control-basemap">
    <unlock-upsell
      v-if="hasLayersUpsell"
      :title="cardUpsellTitle"
      class="upsell-button"
      @upgrade="upgrade"
    />
    <div
      :class="[
        'control-maximized',
        { hide: !isMaximized, basemapCardCollapsed },
      ]"
    >
      <mobile-prototype-card-handle @click="toggleBasemapCardHeight" />
      <div class="main modal">
        <div class="content">
          <div class="basemaps">
            <button
              v-for="basemap in basemaps"
              :key="basemap.id"
              :style="{ backgroundImage: `url(${basemap.image})` }"
              :class="{ selected: basemap.id === currentBasemap }"
              :data-test="basemap.id"
              @click="updateBasemap(basemap.id)"
            >
              <span class="pill">
                <div
                  v-if="basemap.id === currentBasemap"
                  class="checkmark-container"
                >
                  <ys-icon
                    icon="ysCheckmarkSmall"
                    style="color: var(--ys-color-brand-primary)"
                    size="medium"
                  />
                </div>
                {{ basemap.name }}
              </span>
            </button>
          </div>
          <animation-wrap :type="ANIMATION_TYPES.FADE" mode="out-in">
            <imagery-toggles v-if="currentBasemap !== BasemapId.Topo" />
          </animation-wrap>
        </div>
      </div>
    </div>
  </div>
</template>
<!-- eslint-disable vue/block-lang -->
<script setup>
///// imports /////
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import '@onxmaps/yellowstone-ui/ys-icon'
import '@onxmaps/yellowstone-ui/icons/ysCheckmarkSmall'

import AnimationWrap from '@/components/shared/animation-wrap'

import {
  basemap as getBasemap,
  Basemap,
  setBasemap,
  basemapMarketingName,
  triggerBasemapSelectedEvent,
} from '@/services/basemap-service'
import marketingEventService from '@/services/marketing-event-service'
import { useLayers } from '@/services/layer-management-service/composables/use-layers'
import { useHasAccess } from '@/services/feature-access/composables'
import { FishGates } from '@/services/feature-access/fish/types'

import { goToPurchase } from '@/lib/purchase-utils'
import pubsub from '@/lib/pubsub'
import { ANIMATION_TYPES } from '@/lib/enums'
import { PURCHASE_CTA_ORIGINS } from '@/lib/enums-marketing'
import { HUMAN_SKUS } from '@/lib/plans'

import imageryToggles from '@/fish/components/map-controls/basemap/imagery-toggles'
import mobilePrototypeCardHandle from '@/components/mobile-prototyping/mobile-prototype-card-handle.vue'
import unlockUpsell from '@/components/shared/unlock-upsell.vue'

import topo from '@/assets/images/basemap-fish-topo.png'
import hybrid from '@/assets/images/basemap-fish-hybrid.png'
import aerial from '@/assets/images/basemap-fish-aerial.png'

///// refs and variables /////
const { getUserLayerById } = useLayers()

const BasemapId = {
  Topo: 'topo',
  Hybrid: 'hybrid',
  Aerial: 'aerial',
}

const cardUpsellTitle =
  'Unlock onX Fish to get access to <strong>All Map Layers.</strong>'
const { hasAccess: hasLayersAccess } = useHasAccess(FishGates.layer_upsell)

const route = useRoute()
const router = useRouter()

const isMaximized = ref(false)
const basemapCardCollapsed = ref(false)

///// computed /////
const hasLayersUpsell = computed(() => !hasLayersAccess.value)

const currentBasemap = computed(getBasemap)

const basemaps = computed(() => {
  const basemaps = []
  const topoBasemap = getUserLayerById(Basemap.TOPO)
  const hybridBasemap = getUserLayerById(Basemap.HYBRID)
  const aerialBasemap = getUserLayerById(Basemap.AERIAL)

  if (topoBasemap) {
    topoBasemap.image = topo
    basemaps.push(topoBasemap)
  }

  if (hybridBasemap) {
    hybridBasemap.image = hybrid
    basemaps.push(hybridBasemap)
  }

  if (aerialBasemap) {
    aerialBasemap.image = aerial
    aerialBasemap.name = 'Satellite'
    basemaps.push(aerialBasemap)
  }
  return basemaps
})
///// functions /////
const toggleBasemapCardHeight = () => {
  basemapCardCollapsed.value = !basemapCardCollapsed.value
}

const upgrade = () => {
  const sku = HUMAN_SKUS.FISH_ELITE_YEAR
  goToPurchase(
    {
      origin: PURCHASE_CTA_ORIGINS.BASEMAP_CARD,
      detail: 'Upgrade',
    },
    `product=${sku}&membership=elite`
  )
}
const updateBasemap = (id) => {
  marketingEventService.setEventValues('basemap_selected', {
    active_basemap: basemapMarketingName.value,
  })

  setBasemap(id)
  triggerBasemapSelectedEvent()
}
///// watchers /////
watch(route, (to) => {
  if (to.query.basemaps === 'true') {
    isMaximized.value = true
    const queryParams = { ...route.query }
    delete queryParams.basemaps
    router.replace({ query: queryParams })
  }
})

watch(isMaximized, (value) => {
  // add listener to close if clicked anywhere outside of component
  const outsideClickListener = (event) => {
    if (!this.$el.contains(event.target)) {
      isMaximized.value = false
      removeClickListener()
    }
  }

  if (value) {
    document.addEventListener('click', outsideClickListener)
  }

  const removeClickListener = () =>
    document.removeEventListener('click', outsideClickListener)

  // Publish the card change state, so that other UI may react to it as needed
  pubsub.publish('controlBasemapToggled', { active: value })
})
</script>
<style lang="scss" scoped>
@use '@/styles/system/_shadow.scss';

.main {
  margin-top: var(--ys-spacing-250);
}

.basemaps {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 6px;
  margin-bottom: var(--ys-spacing-250);

  button {
    all: unset;
    cursor: pointer;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    height: 70px;
    border-radius: var(--ys-radius-100);
    display: flex;
    align-items: end;
    justify-content: center;
    box-shadow: shadow.$Low;
    .pill {
      padding: var(--ys-spacing-25) var(--ys-spacing-150);
      background-color: var(--ys-color-surface-reverse);
      border-radius: 28px;
      color: var(--ys-color-surface-base);
      margin-bottom: 4px;
      display: flex;
      gap: var(--ys-spacing-25);
    }

    &.selected {
      outline: 4px solid var(--ys-color-brand-primary);
      border: 1px solid var(--ys-color-background-primary);
      .pill {
        background-color: var(--ys-color-brand-primary);
        padding-left: 2px;
        align-items: center;

        .checkmark-container {
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: var(--ys-color-background-primary);
          border-radius: 50%;
          width: 18px;
          height: 18px;
        }
      }
      &:focus {
        border: 1px solid var(--ys-color-brand-primary);
        border: 1px solid var(--ys-color-background-primary);
      }
    }
    &:focus {
      outline: 4px solid var(--ys-color-brand-primary);
      border: 1px solid var(--ys-color-brand-primary);
    }
  }
}

.basemap-header {
  display: flex;
  align-items: end;
  justify-content: space-between;
  margin-top: var(--ys-spacing-50);
  margin-bottom: var(--ys-spacing-250);
}

button.imagery {
  all: unset;
  cursor: pointer;
  width: 100%;

  // Change the margin to padding to enlarge buttons click area
  margin: -16px 0px;
  padding: 16px 0px;

  display: flex;
  justify-content: space-between;
}
</style>
